import type { ComponentPropsWithoutRef, ReactNode, JSX } from 'react';
import { styled, css } from 'styled-components';
import { isNil } from '@wistia/type-guards';
import { mq } from '../../helpers/mq';
import type { ButtonSizes } from '../Button/Button';
import { buttonSizeStyles } from '../Button/buttonStyles';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';

type ButtonGroupButtonSize = ButtonSizes | 'natural';

export type ButtonGroupComponentProps = {
  $align: 'center' | 'left' | 'right';
  $collapse: boolean;
  $fullWidth: boolean;
  $buttonSize: ButtonGroupButtonSize;
};

export type ButtonGroupProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Align buttons to the left or right of their container
   */
  align?: 'center' | 'left' | 'right';
  /**
   * Buttons that will be rendered in grouping
   */
  children?: ReactNode;
  /**
   * Force ButtonGroup to fill the width of it's container
   */
  fullWidth?: boolean;
  /**
   * If true, the button group will collapse to a single column on small screens
   * and the buttons will be stacked on top of each other.
   */
  collapseOnSmallScreens?: boolean;
  /**
   * The size of the buttons in the group. Setting this will override the size of any UI button passed in as a child.
   *
   * @type ResponsiveObject<'lg' | 'md' | 'sm' | 'xl' | 'natural'>
   */
  buttonSize?: ButtonGroupButtonSize | ResponsiveObject<ButtonGroupButtonSize>;
};

const getAlignment = (align: string) => {
  if (align === 'center') {
    return 'center';
  }

  if (align === 'left') {
    return 'flex-start';
  }

  if (align === 'right') {
    return 'flex-end';
  }

  return 'flex-start'; // default alignment
};

const ButtonGroupComponent = styled.div<ButtonGroupComponentProps>`
  display: flex;

  /* this helps ensure that primary buttons appear at the top of the column */
  flex-direction: ${({ $collapse, $align }) => {
    if (!$collapse) {
      return 'row';
    }

    return $align === 'right' ? 'column-reverse' : 'column';
  }};
  gap: var(--wui-space-02);
  align-items: center;
  justify-content: ${({ $align }) => getAlignment($align)};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  ${mq.smAndUp} {
    flex-direction: row;
  }

  &:not([data-wistia-ui-icon-button]) {
    ${mq.smAndDown} {
      width: 100%;
    }
  }

  ${({ $buttonSize }) =>
    $buttonSize !== 'natural' &&
    css`
      button,
      a {
        ${buttonSizeStyles[$buttonSize]}
      }

      [data-wistia-ui-icon-button] {
        height: var(--icon-button-size-${$buttonSize});
        width: var(--icon-button-size-${$buttonSize});
      }
    `}
`;

/**
 * ButtonGroup is a layout component that groups buttons together,
 * with a standardized gap between them.
 */
export const ButtonGroup = ({
  children,
  align = 'left',
  fullWidth = false,
  collapseOnSmallScreens = true,
  buttonSize = 'natural',
  ...props
}: ButtonGroupProps): JSX.Element | null => {
  const responsiveButtonSize = useResponsiveProp(buttonSize);

  if (isNil(children)) {
    return null;
  }

  return (
    <ButtonGroupComponent
      $align={align}
      $buttonSize={responsiveButtonSize}
      $collapse={collapseOnSmallScreens}
      $fullWidth={fullWidth}
      {...props}
    >
      {children}
    </ButtonGroupComponent>
  );
};

ButtonGroup.displayName = 'ButtonGroup';
